import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import {
    ActionItem,
    ActiveElementReference,
    ApiFragment,
    BaseContainer,
    cacheValueWhileSubscribed,
    DialogConfig,
    DialogStyle,
    ElementReference,
    getShareContext,
    isNotNullOrUndefined,
    Metadata,
    PaginatedResults,
    PartialRecord,
    PreviewStatus,
    QueryIteratorStack,
    QueryStateHandler,
    ShDialogComponent,
    throwIfNullOrUndefined,
    UrlQueryParams
} from '@stylehaven/components';
import { isEqual } from 'lodash-es';
import { OutletCloser } from '../../common/services/common/outlet-closer.service';
import { SharedElementsModule } from '../../elements/shared/shared-elements.module';
import { PreviewDialogActionsService } from './preview-dialog-actions.service';

@Component({
    selector: 'med-list-preview-dialog-container',
    templateUrl: 'list-preview-dialog-container.component.html',
    styleUrls: ['list-preview-dialog-container.component.scss'],
    standalone: true,
    imports: [ShDialogComponent, SharedElementsModule, CommonModule]
})
export class ListPreviewModalContainerComponent extends BaseContainer implements OnInit {
    private activeRecord$: Observable<ActiveElementReference | undefined>;
    private queryParams$: Observable<UrlQueryParams>;
    public style: DialogStyle = DialogStyle.ContentOnly;
    public config$: Observable<DialogConfig>;
    public open$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    public queryIteratorId$: Observable<string | undefined>;
    public queryIteratorStack$: Observable<QueryIteratorStack>;
    public paginatedResults$: Observable<PaginatedResults<PartialRecord> | undefined>;
    public contextPageRecordTitle$: Observable<string>;

    private readonly actionsService: PreviewDialogActionsService = inject(PreviewDialogActionsService);
    private readonly outletCloser: OutletCloser = inject(OutletCloser);

    constructor(
        private readonly route: ActivatedRoute,
        private readonly queryStateHandler: QueryStateHandler
    ) {
        super();
        this.initializePreviewState();
    }

    closeModal(error: boolean = false): void {
        this.queryParams$
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(queryParams => this.outletCloser.closeOutlet('preview', queryParams, error));
    }

    private initializePreviewState(): void {
        this.queryIteratorStack$ = this.route.data.pipe(
            map(data => (data.routerState.queryIteratorStack ?? []) as QueryIteratorStack)
        );
        this.queryParams$ = this.route.data.pipe(
            map(data => (data.routerState.queryParams ?? undefined) as UrlQueryParams)
        );
        this.contextPageRecordTitle$ = this.route.data.pipe(
            map(data => (data.routerState.contextPageRecordTitle ?? undefined) as string)
        );
    }

    private initializeQueryIteratorId(): Observable<string> {
        return this.queryIteratorStack$.pipe(
            map(stack => {
                const [last] = stack.slice(-1);
                return last;
            }),
            map(entry => entry?.queryIteratorId)
        );
    }

    ngOnInit(): void {
        this.queryIteratorId$ = this.initializeQueryIteratorId();

        this.activeRecord$ = this.queryIteratorId$.pipe(
            throwIfNullOrUndefined(),
            switchMap(iterator => this.queryStateHandler.getActive(iterator)),
            map(activeRef => {
                if (!activeRef) {
                    throw Error('No active record found. Closing modal.');
                }
                return activeRef;
            }),
            catchError(error => {
                this.closeModal(true);
                return of(undefined);
            }),
            distinctUntilChanged(isEqual)
        );

        const record$: Observable<PartialRecord> = this.activeRecord$.pipe(
            isNotNullOrUndefined(),
            switchMap(activeRef => this.queryStateHandler.getRecord(activeRef)),
            tap(record => this.queryStateHandler.registerConsultation(record.getFragmentId())),
            cacheValueWhileSubscribed(1)
        );

        const shareContext$: Observable<string | undefined> = getShareContext(this.route);

        const actionItems$: Observable<ActionItem[]> = record$.pipe(
            withLatestFrom(this.queryIteratorStack$, shareContext$),
            switchMap(([record, queryIteratorStack, shareContext]) =>
                this.actionsService.getActionItems(new ApiFragment(record.metadata as Metadata), {
                    queryIteratorStack,
                    shareContext
                })
            )
        );

        this.paginatedResults$ = this.activeRecord$.pipe(
            withLatestFrom(this.queryIteratorId$),
            switchMap(([activeRef, queryIterator]) =>
                activeRef && queryIterator
                    ? this.queryStateHandler.getPage(queryIterator, activeRef.current.page)
                    : of(undefined)
            )
        );

        this.config$ = combineLatest([
            this.activeRecord$,
            record$,
            actionItems$,
            this.paginatedResults$,
            this.contextPageRecordTitle$
        ]).pipe(
            withLatestFrom(this.queryIteratorStack$),
            map(([[activeRef, record, actionItems, paginatedResults, contextPageRecordTitle], queryIteratorStack]) => {
                return this.getDialogConfig(
                    activeRef,
                    record,
                    actionItems,
                    queryIteratorStack,
                    paginatedResults,
                    contextPageRecordTitle
                );
            })
        );

        this.actionsService.getPerformedActions().subscribe(performedAction => {
            if (performedAction.action === 'DELETE') {
                this.closeModal();
            }
        });
    }

    private getDialogConfig(
        activeRef: ActiveElementReference | undefined,
        record: PartialRecord,
        recordActions: ActionItem[],
        queryIteratorStack: QueryIteratorStack,
        paginatedResults: PaginatedResults<PartialRecord> | undefined,
        contextPageRecordTitle: string
    ): DialogConfig {
        const configData: any = {
            record,
            recordActions,
            contextPageRecordTitle: contextPageRecordTitle,
            fragmentIndex: paginatedResults ? this.getFragmentIndex(record, paginatedResults) : null,
            fragmentPreviewUrl: record.getImagePreviewUrl(),
            route: this.route.parent,
            state: { queryIteratorStack },
            previewStatus: this.getPreviewStatus(record),
            paginatedResults
        };

        if (activeRef && activeRef.prev) {
            configData.prevAction = () => this.onPrev(activeRef, queryIteratorStack);
        }
        if (activeRef && activeRef.next) {
            configData.nextAction = () => this.onNext(activeRef, queryIteratorStack);
        }

        return {
            data: configData
        };
    }

    private getFragmentIndex(fragment: PartialRecord, paginatedResults: PaginatedResults<PartialRecord>): number {
        return paginatedResults.results.findIndex(frag => frag.getRecordId() === fragment.getRecordId());
    }

    private onPrev(el: ActiveElementReference | undefined, stack: QueryIteratorStack): void {
        if (!el || !el.prev) {
            return;
        }

        this.setNewActiveElement(el.prev);
    }

    private onNext(el: ActiveElementReference | undefined, stack: QueryIteratorStack): void {
        if (!el || !el.next) {
            return;
        }

        this.setNewActiveElement(el.next);
    }

    private setNewActiveElement(newElement: ElementReference): void {
        this.queryIteratorId$
            .pipe(isNotNullOrUndefined(), take(1), takeUntil(this.destroy$))
            .subscribe(queryIteratorId =>
                this.queryStateHandler.setActive(queryIteratorId, newElement.id, newElement.page)
            );
    }

    private getPreviewStatus(record: PartialRecord): PreviewStatus {
        if (record.hasIncompleteBrowse()) {
            return PreviewStatus.Error;
        } else if (record.doesNotHaveBrowse()) {
            return PreviewStatus.NoPreview;
        } else if (record.isProcessing() || record.isBrowseProcessing()) {
            return PreviewStatus.Processing;
        } else {
            return PreviewStatus.Ready;
        }
    }
}
